import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router';
import {
  getDaysBetweenDates,
  getWeeksBetween,
  formatDate,
} from '../../../lib/date';
import { ReactComponent as CrownSvg } from '../../../assets/svg/crown.svg';
import Feedback from '../../../components/feedback';
import Calendar from './calendar';
import request from '../../../lib/request';
import { NAVIGATION } from '../../../types/enum';
import { DASHBOARD } from '../../../types/enum';

const WorkoutTracker = () => {
  const { user, handleCurrentNav, handleDashboardSection } = useOutletContext();
  const navigate = useNavigate();

  const [currentProgram, setCurrentProgram] = useState({});
  const [feedback, setFeedback] = useState([]);
  const [currentChallenges, setCurrentChallenges] = useState([]);

  useEffect(() => {
    if (user?.programRegistered) {
      getCurrentProgram();
    }
    getChallenges();
  }, []);

  const getFeedback = async (programId) => {
    try {
      const res = await request.get(`/workout/feedback?programId=${programId}`);
      if (res.result) {
        setFeedback(res.result);
      }
    } catch (err) {
      console.log('err:: ', err);
    }
  };

  const getCurrentProgram = async () => {
    try {
      const { result } = await request.get('/program');

      if (result) {
        setCurrentProgram(result);
        await getFeedback(result.programId);
      }
    } catch (err) {
      console.log('err:: ', err);
    }
  };

  const getChallenges = async () => {
    try {
      const { result } = await request.get(
        `/user/challenges?status=CURRENT&localDate=${formatDate(new Date())}`
      );
      if (Array.isArray(result) && result.length > 0) {
        const sorted = result.sort((a, b) => {
          return new Date(a.startDate) - new Date(b.startDate);
        });
        setCurrentChallenges(sorted);
      } else {
        setCurrentChallenges([]);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const goToDashboardChallenge = () => {
    navigate('/dashboard');
    handleCurrentNav(NAVIGATION.DASHBOARD)();
    handleDashboardSection(DASHBOARD.CHALLENGE)();
  };

  return (
    <div className='space-y-4 pb-24 p-4'>
      <h1 className='text-2xl font-bold text-white'>Workout Tracker</h1>
      {currentProgram.program && (
        <div
          className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-500/30 to-slate-600/20 border border-slate-400/30 p-4 mt-2 group hover:shadow-lg transition-all duration-300`}
        >
          {/* Decorative elements */}
          <div className='absolute top-0 right-0 w-24 h-24 bg-slate-400/5 rounded-full -translate-y-1/2 translate-x-1/2'></div>
          <div className='absolute bottom-0 left-0 w-32 h-32 bg-slate-400/5 rounded-full translate-y-1/2 -translate-x-1/2'></div>

          {/* Content */}
          <div className='relative z-10'>
            <div className='flex items-center gap-2 mb-3 text-sm'>
              <div className='bg-slate-400/20 p-1 rounded-lg'>
                <CrownSvg className='size-3 text-slate-300' />
              </div>
              <div className='font-bold text-slate-300'>
                {currentProgram.program.title}
              </div>
            </div>

            <div className='space-y-1 text-xs text-white'>
              <div className='flex items-center gap-2'>
                <div className='w-1.5 h-1.5 rounded-full bg-slate-400'></div>
                <span>
                  {formatDate(new Date(currentProgram.startDate))} -{' '}
                  {formatDate(new Date(currentProgram.endDate))} (
                  {
                    getWeeksBetween(
                      currentProgram.startDate,
                      currentProgram.endDate
                    ).length
                  }{' '}
                  weeks)
                </span>
              </div>
              <div className='flex items-center gap-2'>
                <div className='w-1.5 h-1.5 rounded-full bg-slate-400'></div>
                <span>
                  {getDaysBetweenDates(
                    new Date(),
                    new Date(currentProgram.endDate)
                  )}{' '}
                  days to go
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      {currentProgram.program && (
        <Feedback
          type='Workout'
          feedback={feedback}
        />
      )}
      {currentChallenges.length > 0 && (
        <div>
          <h2 className='font-bold text-white mb-2'>Current Challenges</h2>
          <div className='relative flex items-center justify-start gap-2 overflow-x-auto z-10 scrollbar-hidden'>
            {currentChallenges.map((challenge) => (
              <div
                key={challenge.id}
                className='flex-shrink-0 aspect-[2/3] w-[20vw] min-w-[108px] max-w-[140px] relative'
              >
                <img
                  src={challenge.bannerUrl}
                  alt='poster'
                  className='object-cover object-center w-full h-full rounded-md'
                  onClick={goToDashboardChallenge}
                />
                <div className='absolute bottom-0 right-0 bg-carbs/60 px-1 py-[1px] rounded-tl-md rounded-br-md text-xs text-white'>
                  Day{' '}
                  {getDaysBetweenDates(
                    new Date(challenge.startDate),
                    new Date()
                  ) + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <Calendar
        user={user}
        currentProgram={currentProgram}
      />
    </div>
  );
};

export default WorkoutTracker;
